// Prefetch helper pro ClientDetail — pri hover na radek v tabulce (Klienti/Permanentky/LogStBonus)
// fetchneme 4 paralelne (komentare, bookings, invoices, log-st-bonus). Klik = data sou cached → modal otevre instant.
//
// 22.5.2026: rozsireno o invoices + log-st-bonus (PRED tim fetchovaly se az pri open
// → 600ms delay viditelny). Vsechny 4 paralelne, throttle 30s per klient.
//
// SessionStorage cache klice (sjednoceny s ClientDetail.jsx):
//   fw40_client_comments_<klient>     (TTL 5 min)
//   fw40_client_bookings_<klient>     (TTL 5 min)
//   fw40_client_invoices_<klient>     (TTL 5 min)
//   fw40_log_st_bonus_all             (TTL 5 min, sdileny pro vsechny klienty)

import { apiFetch } from "./api.js";

const COMMENTS_PREFIX = "fw40_client_comments_";
const BOOKINGS_PREFIX = "fw40_client_bookings_";
const INVOICES_PREFIX = "fw40_client_invoices_";
const LOG_ST_BONUS_KEY = "fw40_log_st_bonus_all";
const TTL_MS = 5 * 60 * 1000;
// Throttle — max 1 prefetch per klient per 30s (pri hover/leave/hover znovu)
const _lastPrefetch = new Map();
const PREFETCH_THROTTLE_MS = 30 * 1000;

function isCacheValid(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return false;
    const obj = JSON.parse(raw);
    if (!obj || !obj.savedAt) return false;
    return (Date.now() - obj.savedAt) < TTL_MS;
  } catch { return false; }
}

// Debounce timer per klient — pokud user rychle hover-scroll, neprefetchneme
// Drive 150ms, snizene na 0 pro instant prefetch (zadani Lada 16.5.). Throttle 30s
// nad tim zabranuje re-fetch behem rychle skrolovani — to staci.
const _debounceTimers = new Map();
const HOVER_DEBOUNCE_MS = 0;

export function prefetchClientData(klientName) {
  if (!klientName) return;
  // Cancel existujici debounce timer pro tohoto klienta (re-hover)
  if (_debounceTimers.has(klientName)) {
    clearTimeout(_debounceTimers.get(klientName));
  }
  // Naplanuj prefetch po 150ms — pokud user mezitim odjede, prefetch se zrusi
  const timerId = setTimeout(() => {
    _debounceTimers.delete(klientName);
    _doPrefetch(klientName);
  }, HOVER_DEBOUNCE_MS);
  _debounceTimers.set(klientName, timerId);
}

function _doPrefetch(klientName) {
  if (!klientName) return;
  const last = _lastPrefetch.get(klientName) || 0;
  if (Date.now() - last < PREFETCH_THROTTLE_MS) return;
  _lastPrefetch.set(klientName, Date.now());

  // Komentare
  const cKey = COMMENTS_PREFIX + klientName;
  if (!isCacheValid(cKey)) {
    apiFetch(`/api/client-comments?klient=${encodeURIComponent(klientName)}`)
      .then((r) => r.json())
      .then((j) => {
        if (j && Array.isArray(j.comments)) {
          try { sessionStorage.setItem(cKey, JSON.stringify({ comments: j.comments, savedAt: Date.now() })); } catch {}
        }
      })
      .catch(() => {});
  }

  // Bookings
  const bKey = BOOKINGS_PREFIX + klientName;
  if (!isCacheValid(bKey)) {
    apiFetch(`/api/client-bookings?klient=${encodeURIComponent(klientName)}`)
      .then((r) => r.json())
      .then((j) => {
        if (j && !j.error) {
          try { sessionStorage.setItem(bKey, JSON.stringify({ data: j, savedAt: Date.now() })); } catch {}
        }
      })
      .catch(() => {});
  }

  // Invoices — 22.5.2026 prefetch, dříve fetchované až při open ClientDetail
  const iKey = INVOICES_PREFIX + klientName;
  if (!isCacheValid(iKey)) {
    apiFetch(`/api/client-invoices?klient=${encodeURIComponent(klientName)}`)
      .then((r) => r.json())
      .then((j) => {
        if (j && !j.error) {
          try { sessionStorage.setItem(iKey, JSON.stringify({ data: j, savedAt: Date.now() })); } catch {}
        }
      })
      .catch(() => {});
  }

  // Log ST/Bonus — sdílený endpoint pro všechny klienty, fetchujeme 1×
  if (!isCacheValid(LOG_ST_BONUS_KEY)) {
    apiFetch("/api/log-st-bonus?od=epocha")
      .then((r) => r.json())
      .then((j) => {
        if (j) {
          try { sessionStorage.setItem(LOG_ST_BONUS_KEY, JSON.stringify({ data: j, savedAt: Date.now() })); } catch {}
        }
      })
      .catch(() => {});
  }
}
